import React, { useContext, useMemo } from 'react'
import { Controller } from 'react-hook-form'
import CreatableSelect from 'react-select/creatable'
import get from 'lodash.get'
import { FaAsterisk } from 'react-icons/fa'

import SelectStyles from '../../../elem/form/SelectStyles'
import { DataContext } from '../DataContext'
import { Tooltip } from '../../../elem/Tooltip'
import { ExistingDataContext } from './ExistingSubmissionDataContext'
import FieldErrors from './FieldErrors'
import { getApiErrorFromErrorState, getWarningFromErrorState, isChanged } from '../../../../utils/submissions/values'

const mapValuesToOptions = (values, fieldAccessorMap) => {
    if (!values) {
        return []
    }
    const options = values
        .filter(v => v[fieldAccessorMap.value] !== '')
        .map(v => {
            return {
                label: v[fieldAccessorMap.label],
                value:
                    fieldAccessorMap.type === 'number'
                        ? parseFloat(v[fieldAccessorMap.value])
                        : v[fieldAccessorMap.value],
                type: fieldAccessorMap.type,
            }
        })
    return options
}

const onChange = ([selected]) => {
    return { value: selected }
}

const CreatableSingle = ({
    name,
    fieldName,
    values,
    control,
    helper,
    errors,
    fieldAccessorMap,
    formWidth,
    dataAccessor,
    displayLabel,
    rowIdx,
    required,
    registerParams,
    disabled,
    watch,
}) => {
    const { submissionState, activePanel, viewOnly, errorState } = useContext(
        DataContext
    )
    const c = useContext(ExistingDataContext)
    const uploadChanges = c ? c.uploadChanges : null

    const changed = useMemo(
        () =>
            isChanged(
                uploadChanges,
                dataAccessor,
                name,
                rowIdx,
                submissionState
            ),
        [uploadChanges, submissionState, dataAccessor, name, rowIdx]
    )

    const inputName = `${dataAccessor}${
        typeof rowIdx !== 'undefined' ? `[${rowIdx}]` : ''
    }.${name}Select`


    const options = useMemo(() => {
        const value = watch(inputName)
        const o = mapValuesToOptions(values, fieldAccessorMap)
        if (value) {
            if (!values.find(x => x.code === value)) {
                return [...o, value]
            }
        }
        return o
    }, [fieldAccessorMap, watch(inputName)])

    const formError =
        errors && get(errors, inputName) ? get(errors, inputName) : null

    const warning = useMemo(() => {
        return getWarningFromErrorState(errorState, name, rowIdx, dataAccessor)
    }, [errorState, name, activePanel, rowIdx, formError, dataAccessor])

    const apiError = useMemo(() => {
        return getApiErrorFromErrorState(
            errorState,
            name,
            rowIdx,
            dataAccessor,
            formError
        )
    }, [errorState, name, activePanel, rowIdx, formError, dataAccessor])

    return (
        <div
            className={`column ${
                formWidth === 'full' ? 'is-one-third' : 'is-full'
            } no-vertical-padding formInputWrapper`}
        >
            <div className="formInputLayout">
                <div className="field is-horizontal">
                    {displayLabel && (
                        <div className="field-label is-small">
                            <label className="label">
                                <span>{fieldName}</span>
                                {required ? (
                                    <span className="requiredStar has-text-danger">
                                        {<FaAsterisk />}
                                    </span>
                                ) : null}{' '}
                            </label>
                        </div>
                    )}

                    <div className="field-body">
                        <div className={`field ${helper ? 'has-addons' : ''}`}>
                            <div
                                className={`control is-expanded`}
                                data-tip={`${watch(`${inputName}`) ? watch(`${inputName}`).label ? watch(`${inputName}`).label : watch(`${inputName}`) : ''}`}
                                data-for={inputName}
                            >
                                <Controller
                                    as={
                                        <CreatableSelect
                                            className={`select is-multiple is-small is-multiple is-fullwidth reactSelect ${
                                                changed
                                                    ? 'changedSelect'
                                                    : ''
                                            }`}
                                            classNamePrefix="reactSelect"
                                            options={options}
                                            styles={SelectStyles}
                                            menuPlacement="auto"
                                            isClearable
                                            isDisabled={viewOnly || disabled}
                                        />
                                    }
                                    name={inputName}
                                    control={control}
                                    onChange={onChange}
                                    rules={registerParams}
                                />
                                <Tooltip
                                    id={inputName}
                                    extraClass={`tooltip-2`}
                                />
                                <FieldErrors
                                    formError={formError}
                                    warning={warning}
                                    apiError={apiError}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default CreatableSingle
